import type { CustomAttribute } from '@/types';

import { cn } from '../../utils/cn';

import { ButtonElement } from '../../buttonElement';
import { useDreamComponentsContext } from '../../dreamComponentProvider/hooks';
import { paginationContainer, paginationLoadMore } from './LoadMore.css';
import type { PaginateAttributes } from './LoadMore.types';

const buttonNode = {
  type: 'button',
  attrs: {
    id: '6c58b364-4065-44b7-88dc-ef3ce3b7f5d6',
    href: null,
    size: 'normal',
    margin: '0px',
    target: null,
    padding: '12px 16px 12px 16px',
    alignment: 'left',
    borderColor: '#E5E7EB',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: '8px',
    iconPlacement: 'left',
    customTextColor: '#F9FAFB',
    customBackgroundColor: '#030712',
  },
  content: [
    {
      text: 'Load more',
      type: 'text',
    },
  ],
};

const getOverriddenNode = (attributes: PaginateAttributes, isLive: boolean, nodeId?: string) => {
  return {
    ...buttonNode,
    attrs: {
      ...buttonNode.attrs,
      id: nodeId ? `${nodeId}__load_more` : buttonNode.attrs.id,
      text: attributes.paginateButtonText || 'Load more',
      busyText: attributes.paginateButtonBusyText || 'Loading...',
      padding: attributes.paginateButtonPadding,
      borderColor: attributes.paginateButtonBorderColor,
      borderStyle: attributes.paginateButtonBorderStyle,
      borderWidth: attributes.paginateButtonBorderWidth,
      borderRadius: attributes.paginateButtonBorderRadius,
      icon: attributes.paginateButtonIcon,
      alignment: attributes.paginateButtonIconPlacement,
      iconPlacement: attributes.paginateButtonIconPlacement,
      iconColor: attributes.paginateButtonIconColor,
      customTextColor: attributes.paginateButtonTextColor,
      customBackgroundColor: attributes.paginateButtonBackgroundColor,
      hoverCustomBackgroundColor: attributes.paginateButtonHoverBackgroundColor,
      hoverCustomTextColor: attributes.paginateButtonHoverTextColor,
      shadow: attributes.paginateButtonShadow,
      fontFamily: attributes.paginateButtonFontFamily,
      fontSize: attributes.paginateButtonFontSize,
      fontWeight: attributes.paginateButtonFontWeight,
      customAttributes: {
        className: isLive ? 'cursor-pointer' : undefined,
      },
    },
  };
};

interface LoadMoreProps {
  onClick: () => void;
  attributes: PaginateAttributes;
  isBusy: boolean;
  isLive: boolean;
  customAttributes?: CustomAttribute;
  nextPageHref?: string;
  nodeId?: string;
}

export const LoadMore = ({
  onClick,
  attributes,
  isBusy,
  isLive = false,
  customAttributes,
  nextPageHref,
  nodeId,
}: LoadMoreProps) => {
  const overriddenNode = getOverriddenNode(attributes, isLive, nodeId);
  const { className: customAttributesClassName, ...restAttributes } = customAttributes || {};
  // The swap EditableTextSlot does for a text block, off the context directly: this label carries
  // no leaf typography, so the TextLeaf wrapper that component adds would be dead weight.
  const { renderEditableText } = useDreamComponentsContext();
  const editable = nodeId && renderEditableText ? renderEditableText(overriddenNode.attrs.id) : null;

  return (
    <div
      className={cn('flex justify-center w-full items-center', paginationContainer, customAttributesClassName)}
      {...restAttributes}
    >
      <ButtonElement
        onClick={onClick}
        isClientSideNavigation={isLive}
        element={{
          type: 'button',
          attrs: { ...overriddenNode.attrs, url: nextPageHref },
          content: [],
        }}
        // Same convention as the serializer's data attributes: rendered on the public site too, inert
        // there, and what lets Studio's canvas address the button.
        attributes={nodeId ? { 'data-node-id': overriddenNode.attrs.id } : undefined}
        disabled={isBusy}
      >
        {/* Inside the sized span, not around it: this class holds the label at 16px, and an editor
            rendered outside it would open at the button's inherited size. */}
        <span className={cn(paginationLoadMore)}>
          {editable ?? (isBusy ? overriddenNode.attrs.busyText : overriddenNode.attrs.text)}
        </span>
      </ButtonElement>
    </div>
  );
};
